import { civilDate } from "./logs.converter";
import type { SessionLogSummaryModel } from "./logs.model";

/**
 * THE ARITHMETIC BEHIND THE CHART AND THE TWO STAT CARDS. Pure, no React, no formatting —
 * which is what lets it be tested against a fixed "today" instead of against the clock.
 *
 * ═══ ONLY A COMPLETED SESSION COUNTS ═══
 *
 * `GET /v1/logs` returns every row in `session_logs`, and not every row is a session that
 * happened: `POST /v1/sessions/start` opens a row at `started` the moment somebody taps into
 * the timer, and a debrief that never arrives leaves it there for good. Counting those would
 * make "sessions logged" a count of sessions OPENED — the number climbs for a workout the
 * athlete quit in the warm-up, and the chart draws a week they did not train. So the chart
 * and both stats count `completed` only. The LIST still shows every row, because the list is
 * history and an abandoned session is part of it.
 *
 * ═══ THE WIRE'S DATE IS PARSED IN THE CONVERTER, AND EVERY OTHER DATE HERE BY MUTATION ═══
 *
 * `performedOn` is a `YYYY-MM-DD` string on the model, and `civilDate()` in
 * `logs.converter.ts` is the one place it becomes a `Date` — read the note there before
 * reaching for `new Date(<string>)`, which is wrong by a whole day west of Greenwich.
 * `boiler/dates-only-in-converters` refuses that constructor outside a converter for exactly
 * this reason.
 *
 * The WEEK BOUNDARIES below are not wire dates; they are derived from one. They are built
 * with `sameInstant()`, which takes the empty constructor the rule allows and moves it, and
 * the arithmetic uses `setDate`/`setHours` — LOCAL, so it carries across a daylight-saving
 * boundary. Adding `7 * 86_400_000` milliseconds does not, and would slide a bar by an hour
 * twice a year until it landed in the wrong week.
 */

/** The chart's span. Seven bars is what the design draws and what fits at 375pt. */
export const TREND_WEEKS = 7;

export interface WeekBucket {
  /** The Monday the week starts on, at local midnight. */
  start: Date;
  count: number;
}

/** A second Date at the same instant, built without handing the constructor an argument. */
function sameInstant(source: Date): Date {
  const copy = new Date();
  copy.setTime(source.getTime());
  return copy;
}

/** The Monday of the week `date` falls in, at local midnight. Sunday belongs to the week before. */
export function startOfWeek(date: Date): Date {
  const monday = sameInstant(date);
  monday.setHours(0, 0, 0, 0);
  // getDay() is 0 for Sunday; the step back to Monday is 6 for it and day-1 for the rest.
  monday.setDate(monday.getDate() - (monday.getDay() === 0 ? 6 : monday.getDay() - 1));
  return monday;
}

/**
 * SESSIONS PER WEEK, oldest bar first, for the `weeks` weeks ending with the one `now` is in.
 *
 * Every week in the span gets a bucket whether or not it holds anything — a chart that omits
 * an empty week draws a training history nobody had, and the gap IS the information.
 */
export function weekBuckets(
  logs: SessionLogSummaryModel[],
  now: Date,
  weeks: number = TREND_WEEKS,
): WeekBucket[] {
  const current = startOfWeek(now);
  const buckets: WeekBucket[] = [];

  for (let back = weeks - 1; back >= 0; back -= 1) {
    const start = sameInstant(current);
    start.setDate(start.getDate() - back * 7);
    buckets.push({ start, count: 0 });
  }

  const earliest = buckets[0]?.start.getTime() ?? 0;

  for (const log of logs) {
    if (log.outcome !== "completed") continue;
    const week = startOfWeek(civilDate(log.performedOn)).getTime();
    if (week < earliest) continue;
    const bucket = buckets.find((candidate) => candidate.start.getTime() === week);
    if (bucket) bucket.count += 1;
  }

  return buckets;
}

export interface SessionStats {
  /** Completed sessions whose day falls in the calendar month `now` is in. */
  thisMonth: number;
  /** Completed sessions inside the window that was read. */
  logged: number;
  /**
   * TRUE WHEN THE WINDOW WAS FULL AND THE SERVICE SAID THERE IS MORE — so `logged` is a
   * FLOOR and the card renders `100+`. A lifetime total is not on this wire: there is no
   * count endpoint and no aggregate on any view, and printing a windowed number as if it
   * were the total is the kind of quiet lie that gets believed.
   */
  loggedIsFloor: boolean;
}

export function sessionStats(
  logs: SessionLogSummaryModel[],
  now: Date,
  hasMore: boolean = false,
): SessionStats {
  const completed = logs.filter((log) => log.outcome === "completed");
  const month = now.getMonth();
  const year = now.getFullYear();

  return {
    thisMonth: completed.filter((log) => {
      const day = civilDate(log.performedOn);
      return day.getMonth() === month && day.getFullYear() === year;
    }).length,
    logged: completed.length,
    loggedIsFloor: hasMore,
  };
}

/**
 * A BAR'S HEIGHT AS A PERCENTAGE OF THE TALLEST WEEK.
 *
 * A week with sessions in it never renders as nothing: below about 8% of 96px the bar is
 * thinner than the baseline hairline under it, so one session in a month of doubles would
 * read as a week off. Zero stays zero — that is the one case where nothing is the truth.
 */
export function barHeight(count: number, max: number): number {
  if (count === 0 || max === 0) return 0;
  return Math.max(8, Math.round((count / max) * 100));
}
